/*
 * CarbonXtreme creator code box (<cx-creator-code>).
 * Rendered server-side by snippets/cx-creator-code.liquid inside the cart drawer
 * and the cart page. The theme (Concept) re-renders those containers with
 * innerHTML on every cart update, so this element keeps no state of its own:
 * the saved state is drawn by Liquid from cart.attributes._creator_code.
 *
 * Cart attributes written here and by snippets/cx-attribution.liquid:
 *   _creator_code            the code (^[A-Z0-9_-]{3,40}$, same rule in all three capture paths)
 *   _creator_code_source     'typed'         customer typed it in this box (explicit; overrides any earlier value)
 *                            'link'          ?creator= / ?ref= / ?code= landing parameter (explicit; first touch only)
 *                            'discount_link' the code came from the Shopify share-link cookie (/discount/CODE) or
 *                                            was already sitting in cart.discount_codes. Shopify sets that cookie for
 *                                            ANY /discount/ link (EDM, abandoned-cart, a mistyped code) and even for
 *                                            codes that do not exist, so discount_link is a hint, not proof of a
 *                                            creator referral: reconcile it against the creator roster before paying
 *                                            commission. Only 'typed' and 'link' are explicit signals.
 *
 * Flow on Apply (all cart writes serialised through window.cxEnqueue, shared with
 * snippets/cx-attribution.liquid):
 *   1. POST /cart/update.js { attributes: { _creator_code, _creator_code_source: 'typed' } }
 *   2. GET  /discount/<code> (redirect: 'manual') so Shopify stores the discount on the
 *      cart and in the browser cookie for checkout
 *   3. POST /cart/update.js { attributes, sections: [sectionId] } re-asserts the attributes
 *      after the /discount/ read-modify-write and returns discount_codes plus the re-rendered
 *      section HTML. When the code is applicable the theme's cartUpdate event is published with
 *      that HTML (same path as the theme's own discount box), so the drawer / cart page redraw the
 *      subtotal immediately. Not applicable -> no redraw, just the message.
 *   If step 3 fails the code is still saved (steps 1-2 succeeded): show the neutral "saved" copy,
 *   never the "couldn't save" copy.
 * The creator credit (step 1) is independent of whether the discount applies.
 */
(function () {
  'use strict';

  if (!window.customElements || customElements.get('cx-creator-code')) return;

  window.cxCartQueue = window.cxCartQueue || Promise.resolve();
  window.cxEnqueue = window.cxEnqueue || function (task) {
    var run = window.cxCartQueue.then(task, task);
    window.cxCartQueue = run.catch(function () {});
    return run;
  };

  var CODE_RE = /^[A-Z0-9_-]{3,40}$/;
  var MESSAGES = {
    saved: 'Code saved, and your creator gets credit for this order. If a larger discount applies at checkout, you will get that one instead.',
    savedNotApplied: 'Code saved, and your creator gets credit for this order. The best available price is kept for you at checkout.',
    savedUnknown: 'Code saved, and your creator gets credit for this order. The best available price is applied at checkout.',
    invalid: 'Please enter a creator code using letters, numbers, dashes or underscores (3 to 40 characters).',
    failed: 'We could not save the code right now. You can still enter it at checkout.'
  };

  // Typed input is normalised (trimmed, upper-cased, stray characters dropped) before validation.
  // Link / cookie codes are NOT normalised this way (see cx-attribution.liquid): they must match as-is.
  function cleanCode(value) {
    return String(value || '').trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '');
  }

  function updateAttributes(attributes, sectionId) {
    var body = { attributes: attributes };
    if (sectionId) body.sections = [sectionId];
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (!response.ok) throw new Error('cart update ' + response.status);
      return response.json();
    });
  }

  function applyDiscountCookie(code) {
    // Opaque redirect (302) is the expected result; the browser still stores the cookie.
    return fetch('/discount/' + encodeURIComponent(code), { redirect: 'manual', credentials: 'same-origin' })
      .catch(function () { return null; });
  }

  function discountApplied(cart, code) {
    var codes = (cart && cart.discount_codes) || [];
    for (var i = 0; i < codes.length; i++) {
      if (String(codes[i].code || '').toUpperCase() === code && codes[i].applicable !== false) return true;
    }
    return false;
  }

  function hasSectionHtml(cart, sectionId) {
    return !!(sectionId && cart && cart.sections && typeof cart.sections[sectionId] === 'string' && cart.sections[sectionId]);
  }

  function publishCartUpdate(cart) {
    var pubsub = window.theme && window.theme.pubsub;
    if (!pubsub || typeof pubsub.publish !== 'function' || !pubsub.PUB_SUB_EVENTS || !pubsub.PUB_SUB_EVENTS.cartUpdate) return false;
    try {
      pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cx-creator-code', cart: cart });
      return true;
    } catch (e) {
      return false;
    }
  }

  class CxCreatorCode extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-toggle]');
      this.box = this.querySelector('[data-box]');
      this.input = this.querySelector('[data-input]');
      this.apply = this.querySelector('[data-apply]');
      this.msg = this.querySelector('[data-msg]');
      var section = this.closest('[data-section-id]');
      this.sectionId = section ? (section.getAttribute('data-section-id') || '') : '';
      if (!this.toggle || !this.box || !this.input || !this.apply) return;

      this.onToggle = this.openBox.bind(this);
      this.onApply = this.submit.bind(this);
      this.onKeydown = function (event) {
        if (event.key === 'Enter') { event.preventDefault(); this.submit(); }
      }.bind(this);

      this.toggle.addEventListener('click', this.onToggle);
      this.apply.addEventListener('click', this.onApply);
      this.input.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
      if (this.apply) this.apply.removeEventListener('click', this.onApply);
      if (this.input) this.input.removeEventListener('keydown', this.onKeydown);
    }

    openBox() {
      var open = this.box.hidden;
      this.box.hidden = !open;
      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) this.input.focus();
    }

    showMessage(text) {
      if (!this.msg) return;
      this.msg.textContent = text;
      this.msg.hidden = !text;
    }

    submit() {
      if (this.busy) return;
      var code = cleanCode(this.input.value);
      this.input.value = code;
      if (!CODE_RE.test(code)) {
        this.showMessage(MESSAGES.invalid);
        this.input.focus();
        return;
      }

      var self = this;
      var context = this.getAttribute('data-context') || '';
      var sectionId = this.sectionId;
      var attributes = { _creator_code: code, _creator_code_source: 'typed' };
      this.busy = true;
      this.apply.disabled = true;
      this.apply.setAttribute('aria-busy', 'true');

      window.cxEnqueue(function () {
        return updateAttributes(attributes)
          .then(function () { return applyDiscountCookie(code); })
          .then(function () {
            // Steps 1-2 already succeeded: a failure here means "saved, applicability unknown", not "not saved".
            return updateAttributes(attributes, sectionId).catch(function () { return null; });
          });
      })
        .then(function (cart) {
          var applied = !!cart && discountApplied(cart, code);
          var message = !cart ? MESSAGES.savedUnknown : (applied ? MESSAGES.saved : MESSAGES.savedNotApplied);
          var target = self;

          if (applied && cart.item_count > 0 && hasSectionHtml(cart, sectionId) && publishCartUpdate(cart)) {
            // The theme redrew the container (including this element) from the returned section HTML,
            // so the subtotal and discount pills are current; carry the message over to the fresh copy.
            var fresh = document.querySelector('cx-creator-code[data-context="' + context + '"]');
            if (fresh && fresh !== self && fresh.msg) target = fresh;
          }
          if (target === self) {
            self.setAttribute('data-saved-code', code);
            self.box.hidden = true;
            self.toggle.setAttribute('aria-expanded', 'false');
            self.toggle.textContent = 'Use a different creator code';
          }
          target.showMessage(message);
          if (target.toggle) target.toggle.focus();
          document.dispatchEvent(new CustomEvent('cx:attribution-captured', {
            detail: { written: attributes, code: code, source: 'typed', _creator_code: code, _creator_code_source: 'typed' }
          }));
        })
        .catch(function () {
          self.showMessage(MESSAGES.failed);
        })
        .then(function () {
          self.busy = false;
          self.apply.disabled = false;
          self.apply.removeAttribute('aria-busy');
        });
    }
  }

  customElements.define('cx-creator-code', CxCreatorCode);
})();
